import { useEffect, useState, useCallback } from 'react';
import {
  Newspaper, Loader2, AlertCircle, Plus, Pencil, Trash2, X,
} from 'lucide-react';
import {
  listAdminNews, createAdminNews, updateAdminNews, deleteAdminNews,
  type AdminNews,
} from '@/shared/services/adminService';
import { useToast } from '@/shared/contexts/ToastContext';
import { DataTable, type DataTableColumn } from '@/shared/components/ui/DataTable';
import { RowActions } from '@/shared/components/ui/RowActions';

export default function NewsPage() {
  const { notify } = useToast();
  const [items, setItems] = useState<AdminNews[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<AdminNews | null>(null);
  const [creating, setCreating] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try { setItems(await listAdminNews()); }
    catch (err: any) { setError(err?.detail || err?.message || 'Erro'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (id: number) => {
    if (!confirm('Deletar essa notícia?')) return;
    try {
      await deleteAdminNews(id);
      notify('success', 'Deletada');
      await fetchData();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    }
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
            <Newspaper className="text-amber-400" size={26} />
            Notícias
          </h1>
          <p className="text-sm text-white/65 mt-1">
            {items.length.toLocaleString('pt-BR')} publicações · EventData.web_news
          </p>
        </div>
        <button
          onClick={() => setCreating(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold
                     btn-primary
                     transition cursor-pointer"
        >
          <Plus size={14} />
          Nova Notícia
        </button>
      </div>

      {error && (
        <div className="surface-card rounded-xl p-4 mb-4 flex items-start gap-3 border-red-500/30">
          <AlertCircle size={18} className="text-red-400 shrink-0 mt-0.5" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      <DataTable<AdminNews>
        columns={[
          { key: 'title', label: 'Notícia', width: '1fr',
            cell: (n) => (
              <div className="min-w-0">
                <p className="text-sm font-bold text-white truncate">{n.title || '(sem título)'}</p>
                {n.subTitle && <p className="text-[11px] text-white/65 mt-0.5 truncate">{n.subTitle}</p>}
              </div>
            ) },
          { key: 'createdDate', label: 'Criada em', width: '180px',
            cell: (n) => (
              <span className="text-[11px] text-white/45 font-mono">
                ID {n.id} · {n.createdDate ? new Date(n.createdDate).toLocaleString('pt-BR') : '—'}
              </span>
            ) },
          { key: 'actions', label: 'Ações', width: '60px', align: 'right', mobileHidden: true,
            cell: (n) => (
              <RowActions
                actions={[
                  { label: 'Editar', icon: <Pencil size={13} />, onClick: () => setEditing(n), variant: 'warning' },
                  { label: 'Remover', icon: <Trash2 size={13} />, onClick: () => handleDelete(n.id), variant: 'danger' },
                ]}
              />
            ) },
        ]}
        rows={items}
        rowKey={(n) => n.id}
        loading={loading}
        empty="Nenhuma notícia ainda"
        mobileTitle={(n) => n.title || '(sem título)'}
        mobileSubtitle={(n) => n.subTitle ?? ''}
        mobileActions={(n) => (
          <RowActions
            actions={[
              { label: 'Editar', icon: <Pencil size={13} />, onClick: () => setEditing(n), variant: 'warning' },
              { label: 'Remover', icon: <Trash2 size={13} />, onClick: () => handleDelete(n.id), variant: 'danger' },
            ]}
            align="left"
            
          />
        )}
      />

      {(editing || creating) && (
        <NewsEditor
          news={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={async () => { await fetchData(); setEditing(null); setCreating(false); }}
        />
      )}
    </div>
  );
}

function NewsEditor({
  news, onClose, onSaved,
}: { news: AdminNews | null; onClose: () => void; onSaved: () => Promise<void> }) {
  const { notify } = useToast();
  const [title, setTitle] = useState(news?.title || '');
  const [subTitle, setSubTitle] = useState(news?.subTitle || '');
  const [image, setImage] = useState(news?.image || '');
  const [html, setHtml] = useState(news?.html || '');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!title.trim()) { notify('error', 'Título obrigatório'); return; }
    setBusy(true);
    try {
      const payload = { title, subTitle: subTitle || undefined, image: image || undefined, html: html || undefined };
      if (news) {
        await updateAdminNews(news.id, payload);
        notify('success', 'Notícia atualizada');
      } else {
        await createAdminNews(payload);
        notify('success', 'Notícia criada');
      }
      await onSaved();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro ao salvar');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="surface-card rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-white">{news ? 'Editar' : 'Nova'} Notícia</h2>
          <button onClick={onClose} className="text-white/55 hover:text-white"><X size={18} /></button>
        </div>

        <div className="space-y-3">
          <Field label="Título" value={title} onChange={setTitle} />
          <Field label="Subtítulo" value={subTitle} onChange={setSubTitle} />
          <Field label="Imagem (URL)" value={image} onChange={setImage} mono />
          <div>
            <label className="text-[11px] text-white/55 mb-1 block">HTML / Conteúdo</label>
            <textarea
              value={html}
              onChange={(e) => setHtml(e.target.value)}
              rows={10}
              className="w-full bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2
                         text-sm text-white font-mono resize-y"
            />
          </div>
        </div>

        <div className="flex gap-2 mt-5">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-lg bg-white/[0.04] text-white/75
                       hover:bg-white/[0.08] transition cursor-pointer text-sm"
          >
            Cancelar
          </button>
          <button
            onClick={save}
            disabled={busy}
            className="flex-1 px-4 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-400
                       text-[#0d141b] font-bold text-sm transition cursor-pointer
                       disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({
  label, value, onChange, mono,
}: { label: string; value: string; onChange: (v: string) => void; mono?: boolean }) {
  return (
    <div>
      <label className="text-[11px] text-white/55 mb-1 block">{label}</label>
      <input
        type="text" value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white ${mono ? 'font-mono' : ''}`}
      />
    </div>
  );
}
